

























































import ZenButton from '@reciprocity/button';
import GenericCard from '@/components/cards/GenericCard.vue';
import Layout from './layout/Layout.vue';
import GridItemBuilder from './layout/items/GridItemBuilder.vue';
import { LayoutSettings, ComponentTypes, getRequiredConfig, getDefaultPosition } from './layout/model';
import { ModalSize } from '@/components/zen_modal/types';
import AsyncComponentFactory from './layout/items/AsyncComponent';
import { saveAttrLayout, removeLayoutSections } from '@/utils/crudActions';

export default {
  name: 'CustomAttributesConfigModal',
  components: {
    GenericCard,
    Layout,
    GridItemBuilder,
    ZenButton,
    DefaultItem: AsyncComponentFactory(import('./layout/items/DefaultItem.vue')),
    SectionItem: AsyncComponentFactory(import('./layout/items/SectionItem.vue')),
  },
  props: {
    csrfToken: {
      type: String,
      required: true,
    },
    objectType: {
      type: String,
      required: true,
    },
    attributes: {
      type: Object,
      required: true,
    },
    layout: {
      type: Object,
      required: false,
      default: () => {},
    },
    redirectUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    let settings: LayoutSettings = {
      columns: 4,
      itemsPerRow: 4,
      allowNewItems: true,
      verticalCompact: true,
    };
    return {
      layoutSettings: settings,
      items: [],
      saving: false,
      savingAndClosing: false,
      sectionIds: [],
      isRestoreDisabled: true,
    };
  },
  computed: {
    title() {
      return `Drag & resize to change ${this.attributes.title} custom attributes layout`;
    },
    listeners() {
      const vm = this;
      return Object.assign(
        {},
        // Include listeners from the parent
        this.$listeners,
        // Custom or redefined listeners
        {
          removeItem(item) {
            vm.onRemoveItem(item);
          },
          changedItem() {
            vm.onLayoutChanged();
          },
        }
      );
    },
    attributesLayout() {
      return this.layout.attributes;
    },
    sectionsLayout() {
      return this.layout.sections;
    },
    objectLink() {
      return `/sor/listing/${this.objectType}`;
    },
    isSavingChanges() {
      return this.saving || this.savingAndClosing;
    },
  },
  created() {
    if (this.$parent) {
      this.$parent.$on('close', this.close);
    }
    this.$emit('updateConfig', { size: ModalSize.Large });
    this.setItems();
  },
  methods: {
    setItems() {
      const alreadySaved = this.attributesLayout.length > 0;
      const items = this.attributes.cads.map(item => {
        const layout = this.attributesLayout.find(attr => attr.attrId === item.id);
        if (!layout) {
          let config = getRequiredConfig(item.id);
          if (alreadySaved) {
            // if its a new attribute, put it on top left
            const defaultPosition = getDefaultPosition();
            config = Object.assign(config, defaultPosition);
          }

          return {
            ...item,
            ...config,
          };
        }

        return {
          ...item,
          ...layout.config,
        };
      });

      const sectionMap = {};
      this.sectionsLayout.forEach(section => {
        sectionMap[section.id] = {
          ...section.config,
          title: section.title,
          id: section.id,
          i: `section_${section.id}`,
          componentType: ComponentTypes.section,
          isResizable: false,
          isDraggable: true,
        };
      });

      items.splice(0, 0, ...Object.values(sectionMap));
      this.items = items;
      this.sectionIds.splice(0, this.sectionIds.length);
      this.isRestoreDisabled = true;
    },
    restoreLayout() {
      this.setItems();
    },
    save() {
      this.saving = true;
      this.saveChanges().finally(() => {
        this.saving = false;
      });
    },
    async saveChanges() {
      try {
        const data = this.buildLayout();
        if (this.sectionsWithNoAttributes(data.sections)) {
          window.GGRC.Notify({
            type: 'warning',
            message: `Please fill your empty sections.`,
          });
          return;
        }

        const updatedLayout = await this.saveAttrLayout({
          csrf: this.csrfToken,
          url: '/api/v2/attr_layout',
          data: { ...data },
        });

        this.updateLayout(updatedLayout);

        if (this.sectionIds.length) {
          await removeLayoutSections({
            csrf: this.csrfToken,
            url: '/api/v2/attr_layout_section/delete',
            data: { sectionIds: this.sectionIds },
          });
          this.sectionIds.splice(0, this.sectionIds.length);
        }

        window.GGRC.Notify({
          type: 'success',
          message: `Layout successfuly updated.`,
        });

        // Track attr layout changes
        if (window.analytics) {
          analytics.track('Attr Layout Changed', {
            objectType: this.objectType,
            sections: data.sections.length,
          });
        }

        return true;
      } catch (error) {
        window.GGRC.Notify({
          type: 'error',
          message: `Oops, something went wrong.`,
        });
      }
    },
    saveAndClose() {
      this.savingAndClosing = true;
      this.saveChanges()
        .then(success => {
          if (success) this.close();
        })
        .finally(() => {
          this.savingAndClosing = false;
        });
    },
    sectionsWithNoAttributes(sections) {
      if (sections) {
        const empty = sections.filter(sec => !sec.attributes.length);
        return empty.length;
      }
    },
    updateLayout(layout) {
      this.layout.sections = layout.sections || [];
      const attributes = layout.attributes || [];
      this.layout.sections.forEach(section => {
        attributes.push(...section.attributes);
      });

      this.layout.attributes = attributes;
      this.isRestoreDisabled = true;
    },
    getSectionByLocation(layout) {
      return this.items.find(item => item.componentType === ComponentTypes.section && this.sameLayout(layout, item));
    },
    sameLayout(l1, l2) {
      return l1.x === l2.x && l1.y === l2.y && l1.w === l2.w && l1.h === l2.h;
    },
    buildLayout() {
      const data = {
        sections: [],
        attributes: [],
      };

      const sorted = this.items.sort((a, b) => {
        return a.y - b.y;
      });

      let section;
      sorted.forEach(item => {
        if (item.componentType === ComponentTypes.section) {
          section = {
            title: item.title,
            config: {
              h: item.h,
              w: item.w,
              x: item.x,
              y: item.y,
            },
            attributes: [],
          };
          if (item.id) {
            section.id = item.id;
          }
          data.sections.push(section);
        } else {
          const { x, y, w, h } = item;
          let attribute = {
            attr_id: item.id,
            attr_type: 'CA',
            config: {
              x,
              y,
              w,
              h,
            },
            object_type: this.objectType,
          };
          if (section) {
            section.attributes.push(attribute);
          } else {
            data.attributes.push(attribute);
          }
        }
      });

      return data;
    },
    addItem(item: GridItemType) {
      this.items.push(item);
      this.onLayoutChanged();
    },
    onRemoveItem(item: GridItemType) {
      const itemIndex = this.items.findIndex(i => i.i === item.i);
      if (itemIndex >= 0) {
        this.items.splice(itemIndex, 1);
        if (item.id) {
          this.sectionIds.push(item.id);
        }
      }
      this.onLayoutChanged();
    },
    close() {
      window.location.href = this.redirectUrl;
    },
    onLayoutChanged() {
      this.isRestoreDisabled = false;
    },
    saveAttrLayout,
  },
};
